import { parseOrThrow } from "_shared/_utilities/zod-schema-parser";
import AHBoolean from "_shared/booleans/boolean";
import DateSchema from "_shared/dates/date-schema";
import EntityMember from "_shared/entity-members/entity-member";
import EntityMemberSchema from "_shared/entity-members/entity-member-schema";
import FeeScheduleCategory from "_shared/fee-schedules/categories/fee-schedule-category";
import FeeScheduleCategorySchema from "_shared/fee-schedules/categories/fee-schedule-category-schema";
import Guid from "_shared/guids/guid";
import Percent from "_shared/percentages/percent";
import {
  FeeScheduleCategoryMaskedCurrency,
  MaskedContracts,
  MaskedDiscount,
  MaskedFeeSchedule,
  MaskedTeam,
} from "work/_shared/masked-fields";
import WorkDocumentSchema from "work/work-agreements/contracts/_schemas/work-document-schema";
import WorkDocument from "work/work-agreements/contracts/work-document";
import ProjectDescription from "work/work-agreements/project-description/project-description";
import ProjectName from "work/work-agreements/project-name/project-name";
import ClientTeamSchema from "work/work-agreements/teams/client-team/_schema/client-team-schema";
import ClientTeam from "work/work-agreements/teams/client-team/client-team";
import VendorTeamSchema from "work/work-agreements/teams/vendor-team/_schemas/vendor-team-schema";
import VendorTeam from "work/work-agreements/teams/vendor-team/vendor-team";
import { z } from "zod";

export type MaskedField = z.infer<typeof MaskedFieldZ>;
export type WorkAgreementJSON = z.infer<typeof WorkAgreementJSONZ>;
export type WorkAgreementParams = z.infer<typeof WorkAgreementZ>;

export const MaskedFieldZ = z.object({ isMasked: z.literal(true) });

export const WorkAgreementJSONZ = z.object({
  id: z.uuid().nullish(),
  RFPId: z.uuid().nullish(),
  name: z.string().nullish(),
  description: z.string().nullish(),
  clientTeam: z
    .intersection(ClientTeamSchema.ClientTeamJSONZ.optional(), z.object({ isMasked: z.boolean() }))
    .nullish(),
  additionalVendors: z.array(EntityMemberSchema.EntityMemberJSONZ).nullish(),
  vendorTeam: z
    .intersection(VendorTeamSchema.VendorTeamJSONZ.optional(), z.object({ isMasked: z.boolean() }))
    .nullish(),
  teamRestricted: z
    .object({
      isMasked: z.boolean(),
      value: z.boolean().nullish(),
    })
    .nullish(),
  feeSchedule: z.object({
    isMasked: z.boolean(),
    categories: z.array(FeeScheduleCategorySchema.FeeScheduleCategoryJSONZ).nullish(),
  }),
  startDate: z.iso.datetime({ offset: true }).nullish(),
  endDate: z.iso.datetime({ offset: true }).nullish(),
  discount: z.object({ percent: z.number().nullable(), isMasked: z.boolean() }).nullish(),
  clientPolicies: z.object({
    isMasked: z.boolean(),
    documents: z.array(
      z.object({
        documentId: z.uuid().nullish(),
        documentInfo: WorkDocumentSchema.WorkDocumentJSONZ,
        replacesId: z.uuid().nullish()
      })
    ).optional()
  }).nullish(),
  vendorPolicies: z.object({
      isMasked: z.boolean(),
      documents: z.array(
        z.object({
          documentId: z.uuid().nullish(),
          documentInfo: WorkDocumentSchema.WorkDocumentJSONZ,
          replacesId: z.uuid().nullish()
        })
      ).optional()
    }).nullish(),
  conflictsCheckWaived: z
    .object({
      isMasked: z.boolean(),
      value: z.boolean().nullish(),
    })
    .nullish(),
  conflicts: z.object({
    isMasked: z.boolean(),
    documents: z.array(
      z.object({
        documentId: z.uuid().nullish(),
        documentInfo: WorkDocumentSchema.WorkDocumentJSONZ,
        replacesId: z.uuid().nullish(),
      })
    ).optional()
  }).nullish(),
  clientTeamTemplateIds: z.array(z.uuid()).nullish(),
  vendorTeamTemplateIds: z.array(z.uuid()).nullish(),
  clientFeeScheduleTemplateIds: z.array(z.uuid()).nullish(),
  vendorFeeScheduleTemplateIds: z.array(z.uuid()).nullish(),
  hireDate: z.iso.datetime({ offset: true }).nullish(),
  archiveDate: z.iso.datetime({ offset: true }).nullish(),
});

export const WorkAgreementZ = z
  .object({
    id: z.instanceof(Guid).optional(),
    RFPId: z.instanceof(Guid).optional(),
    name: z.instanceof(ProjectName).optional(),
    description: z.instanceof(ProjectDescription).optional(),
    clientTeam: z.instanceof(ClientTeam).or(z.instanceof(MaskedTeam)).optional(),
    additionalVendors: z.array(z.instanceof(EntityMember)).optional(),
    vendorTeam: z.instanceof(VendorTeam).or(z.instanceof(MaskedTeam)).optional(),
    teamRestricted: z.instanceof(AHBoolean).or(z.instanceof(MaskedTeam)).optional(),
    feeSchedule: z
      .union([
        z.union([
          z.array(z.instanceof(FeeScheduleCategory)), 
          z.array(z.instanceof(FeeScheduleCategoryMaskedCurrency))
        ]),
        z.instanceof(MaskedFeeSchedule),
      ])
      .optional(),
    startDate: DateSchema.type.nullable().optional(),
    endDate: DateSchema.type.nullable().optional(),
    discount: z.union([z.custom<Percent>(), z.instanceof(MaskedDiscount)]).optional(),
    clientPolicies: z.union([z.array(z.instanceof(WorkDocument)), z.instanceof(MaskedContracts)]).optional(),
    vendorPolicies: z
      .union([z.array(z.instanceof(WorkDocument)).optional(), z.instanceof(MaskedContracts)])
      .optional(),
    conflictsCheckWaived: z.union([z.custom<AHBoolean>(), z.instanceof(MaskedContracts)]).optional(),
    conflicts: z.union([z.array(z.instanceof(WorkDocument)), z.instanceof(MaskedContracts)]).optional(),
    clientTeamTemplateIds: z.array(z.instanceof(Guid)).optional(),
    vendorTeamTemplateIds: z.array(z.instanceof(Guid)).optional(),
    clientFeeScheduleTemplateIds: z.array(z.instanceof(Guid)).optional(),
    vendorFeeScheduleTemplateIds: z.array(z.instanceof(Guid)).optional(),
    hireDate: DateSchema.type.optional(),
    archiveDate: DateSchema.type.optional(),
  })
  .readonly();

export function validate(values: unknown): WorkAgreementParams {
  return parseOrThrow(WorkAgreementZ, values, {
    logError: true,
    schemaName: "WorkAgreement",
    operation: "validate",
    payloadSample: values,
  });
}

export function validateJSON(json: unknown): WorkAgreementJSON {
  return parseOrThrow(WorkAgreementJSONZ, json, {
    logError: true,
    schemaName: "WorkAgreement",
    operation: "validateJSON",
    payloadSample: json,
  });
}

export function isValid(values: unknown): values is WorkAgreementParams {
  try {
    validate(values);
    return true;
  } catch {
    return false;
  }
}

export function isValidJSON(json: unknown): json is WorkAgreementJSON {
  try {
    validateJSON(json);
    return true;
  } catch {
    return false;
  }
}
